import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { useT } from '../../hooks/useTranslation';

const STORAGE_KEY = 'haaziko_cookie_consent';

/** Google Consent Mode v2 frissítése (a gtag() az index.html-ben definiált, a dataLayer-be ír). */
const updateConsent = (granted: boolean) => {
  const g = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof g !== 'function') return;
  const v = granted ? 'granted' : 'denied';
  g('consent', 'update', { ad_storage: v, ad_user_data: v, ad_personalization: v, analytics_storage: v });
};

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const t = useT();

  useEffect(() => {
    let stored: string | null = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* privát mód */ }
    if (stored) return;

    let cancelled = false;
    const reveal = () => { if (!cancelled) setIsVisible(true); };

    // Várunk az első felhasználói interakcióra (scroll/click/touch), így a
    // cookie sáv nem kerül be a Lighthouse LCP-mérésébe a kezdeti ~5s ablakban.
    // Fallback: 8s után mindenképp megjelenik.
    const fallback = window.setTimeout(reveal, 8000);
    const onInteraction = () => { window.clearTimeout(fallback); reveal(); cleanup(); };
    const cleanup = () => {
      window.removeEventListener('scroll', onInteraction);
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
    };

    window.addEventListener('scroll', onInteraction, { once: true, passive: true });
    window.addEventListener('pointerdown', onInteraction, { once: true });
    window.addEventListener('keydown', onInteraction, { once: true });

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      cleanup();
    };
  }, []);

  const close = (value: 'accepted' | 'declined') => {
    try { localStorage.setItem(STORAGE_KEY, value); } catch { /* privát mód */ }
    updateConsent(value === 'accepted');
    setClosing(true); // kiúszó animáció, utána unmount (onAnimationEnd)
  };

  if (!isVisible) return null;

  return (
    <div
      onAnimationEnd={() => { if (closing) { setIsVisible(false); setClosing(false); } }}
      className={`fixed bottom-0 left-0 right-0 z-150 bg-forest text-sand px-6 py-5 shadow-2xl ${closing ? 'cookie-out' : 'cookie-in'}`}
      role="dialog"
      aria-label="Cookie hozzájárulás"
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-10">
        <p className="text-sm text-sand/75 font-light flex-1 leading-relaxed">
          {t.cookie.text}{' '}
          <a href="/adatkezeles" className="text-gold underline underline-offset-2 hover:text-sand transition-colors">
            {t.cookie.privacyLink}
          </a>
        </p>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => close('declined')}
            className="text-xs uppercase tracking-widest text-sand/80 hover:text-sand transition-colors px-3 py-2"
          >
            {t.cookie.necessary}
          </button>
          <button
            onClick={() => close('accepted')}
            className="text-xs uppercase tracking-widest bg-sand text-forest px-6 py-3 hover:bg-gold transition-colors duration-300 font-medium"
          >
            {t.cookie.accept}
          </button>
          <button
            onClick={() => close('declined')}
            aria-label="Bezárás"
            className="text-sand/35 hover:text-sand transition-colors ml-1"
          >
            <X size={17} />
          </button>
        </div>
      </div>
    </div>
  );
};
